import { ArrowDownSmallIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';

import { Input, type InputProps } from '../input';
import { Loading } from '../loading';
import { Menu, MenuItem, type MenuProps } from '../menu';
import * as styles from './styles.css';

export type SelectOption = {
  label: string;
  value: string;
  description?: string;
  disabled?: boolean;
};

type BaseSelectProps = Omit<
  InputProps,
  'value' | 'onChange' | 'endFix' | 'preFix' | 'type'
> & {
  options: SelectOption[];
  loading?: boolean;
  emptyContent?: ReactNode;
  menuOptions?: Omit<MenuProps, 'children' | 'items'>;
  renderOption?: (option: SelectOption, selected: boolean) => ReactNode;
};

interface SingleSelectProps extends BaseSelectProps {
  multiple?: false;
  value?: string;
  onChange?: (value: string, option: SelectOption) => void;
  renderValue?: (option?: SelectOption) => string;
}

interface MultipleSelectProps extends BaseSelectProps {
  multiple: true;
  value?: string[];
  onChange?: (value: string[], options: SelectOption[]) => void;
  renderValue?: (options: SelectOption[]) => string;
}

export type SelectProps = SingleSelectProps | MultipleSelectProps;

export const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(
  {
    multiple = false,
    value,
    options,
    onChange,
    loading,
    disabled,
    emptyContent = '暂无可选项',
    menuOptions,
    renderValue,
    renderOption,
    className,
    inputStyle,
    style,
    ...inputProps
  },
  ref
) {
  const [open, setOpen] = useState(false);
  const selectedValues = useMemo(
    () =>
      new Set(
        multiple
          ? Array.isArray(value)
            ? value
            : []
          : typeof value === 'string' && value
            ? [value]
            : []
      ),
    [multiple, value]
  );
  const selectedOptions = useMemo(
    () => options.filter(option => selectedValues.has(option.value)),
    [options, selectedValues]
  );
  const selectedOption = selectedOptions[0];

  const {
    rootOptions,
    contentOptions,
    portalOptions,
    noPortal,
    ...otherMenuOptions
  } = menuOptions ?? {};

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      rootOptions?.onOpenChange?.(nextOpen);
    },
    [rootOptions]
  );

  return (
    <Menu
      items={
        options.length ? (
          options.map(option => {
            const selected = selectedValues.has(option.value);
            return (
              <MenuItem
                key={option.value}
                selected={selected}
                disabled={option.disabled}
                block
                onSelect={event => {
                  if (option.disabled) {
                    return;
                  }

                  if (multiple) {
                    event.preventDefault();
                    const currentValues = Array.isArray(value) ? value : [];
                    const nextValues = selected
                      ? currentValues.filter(item => item !== option.value)
                      : [...currentValues, option.value];
                    const nextOptions = options.filter(candidate =>
                      nextValues.includes(candidate.value)
                    );
                    (onChange as MultipleSelectProps['onChange'])?.(
                      nextValues,
                      nextOptions
                    );
                    return;
                  }

                  (onChange as SingleSelectProps['onChange'])?.(
                    option.value,
                    option
                  );
                }}
              >
                {renderOption ? (
                  renderOption(option, selected)
                ) : (
                  <div className={styles.optionContent}>
                    <span className={styles.optionLabel}>{option.label}</span>
                    {option.description ? (
                      <span className={styles.optionDescription}>
                        {option.description}
                      </span>
                    ) : null}
                  </div>
                )}
              </MenuItem>
            );
          })
        ) : (
          <div className={styles.emptyState}>{emptyContent}</div>
        )
      }
      noPortal={noPortal}
      portalOptions={portalOptions}
      rootOptions={{
        modal: false,
        ...rootOptions,
        onOpenChange: handleOpenChange,
      }}
      contentOptions={{
        sideOffset: 6,
        align: 'start',
        ...contentOptions,
        className: clsx(styles.content, contentOptions?.className),
      }}
      {...otherMenuOptions}
    >
      <div
        ref={ref}
        className={styles.trigger}
        style={{ pointerEvents: disabled || loading ? 'none' : undefined }}
      >
        <Input
          {...inputProps}
          value={
            multiple
              ? (renderValue as MultipleSelectProps['renderValue'])?.(
                  selectedOptions
                ) ?? selectedOptions.map(option => option.label).join('、')
              : (renderValue as SingleSelectProps['renderValue'])?.(
                  selectedOption
                ) ?? selectedOption?.label ?? ''
          }
          readOnly
          disabled={disabled || loading}
          tabIndex={-1}
          className={className}
          style={style}
          inputStyle={{
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
            pointerEvents: 'none',
            ...inputStyle,
          }}
          endFix={
            <span className={clsx(styles.indicator, open && styles.indicatorOpen)}>
              {loading ? <Loading size={14} /> : <ArrowDownSmallIcon />}
            </span>
          }
        />
      </div>
    </Menu>
  );
});
